// app/(auth)/login/page.tsx
"use client"

import { useState } from "react"

export default function LoginPage() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const adminEmail = process.env.NEXT_PUBLIC_ADMIN_EMAIL ?? "admin@yourcompany.com"
  const allowedDomain = adminEmail.split("@")[1]
  const accountHint = allowedDomain ? `@${allowedDomain}` : "your work Google Workspace"
  const helpHref = `mailto:${adminEmail}?subject=Canopy access request`

  async function handleGoogleSignIn() {
    setLoading(true)
    setError(null)
    try {
      const callbackURL = `${window.location.origin}/dashboard`

      const response = await fetch("/api/auth/sign-in/social", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          provider: "google",
          callbackURL,
          errorCallbackURL: `${window.location.origin}/login`,
        }),
      })

      const data = (await response.json().catch(() => null)) as
        | { url?: string; redirect?: boolean; message?: string }
        | null

      if (!response.ok || !data?.url) {
        throw new Error(data?.message || "Sign-in failed")
      }

      window.location.href = data.url
    } catch (err) {
      setError(`Sign-in failed. Use your ${accountHint} Google account.`)
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-sm mx-4 rounded-2xl border border-white/10 bg-card p-8 shadow-xl shadow-black/10 flex flex-col gap-6 items-center">
      <img
        src="/logo-full.svg"
        alt="Canopy"
        className="w-full max-w-[200px] h-auto object-contain"
      />

      <div className="text-center space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Sign in to Canopy</h1>
        <p className="text-sm leading-6 text-muted-foreground">
          Continue with your <span className="font-medium text-foreground">{accountHint}</span> Google account.
          Personal Gmail accounts will not work.
        </p>
      </div>

      {error && (
        <div className="w-full px-4 py-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center">
          {error}
        </div>
      )}

      <button
        onClick={handleGoogleSignIn}
        disabled={loading}
        className="inline-flex w-full items-center justify-center gap-3 rounded-xl bg-foreground px-4 py-3 text-sm font-semibold text-background transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 shrink-0">
          <path
            fill="#4285F4"
            d="M21.6 12.23c0-.68-.06-1.33-.17-1.96H12v3.7h5.39a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.97-4.34 2.97-7.27Z"
          />
          <path
            fill="#34A853"
            d="M12 22c2.7 0 4.96-.9 6.61-2.43l-3.24-2.51c-.9.6-2.05.96-3.37.96-2.59 0-4.79-1.75-5.57-4.1H3.08v2.57A10 10 0 0 0 12 22Z"
          />
          <path
            fill="#FBBC05"
            d="M6.43 13.92A5.98 5.98 0 0 1 6.12 12c0-.67.11-1.31.31-1.92V7.51H3.08A10 10 0 0 0 2 12c0 1.61.39 3.14 1.08 4.49l3.35-2.57Z"
          />
          <path
            fill="#EA4335"
            d="M12 5.98c1.47 0 2.79.5 3.83 1.48l2.87-2.87C16.95 2.95 14.7 2 12 2a10 10 0 0 0-8.92 5.51l3.35 2.57c.78-2.35 2.98-4.1 5.57-4.1Z"
          />
        </svg>
        {loading ? "Signing in..." : "Continue with Google"}
      </button>

      <div className="w-full rounded-xl border border-border/70 bg-muted/40 px-4 py-3 text-left text-xs leading-5 text-muted-foreground">
        <p className="font-medium text-foreground">Access is limited to {accountHint} users.</p>
        <p>If you were invited but cannot sign in, contact <a className="underline underline-offset-2 hover:text-foreground" href={helpHref}>{adminEmail}</a>.</p>
      </div>
    </div>
  )
}
